<script setup lang="ts">
import { computed, inject, Ref } from 'vue'
import {
  type Account,
  type AccountResult,
  type Parallel,
  type Supporter,
  Collapsible,
  Dropdown,
  PlatformIcon,
  TheTooltip
} from '@wastedondestiny/destiny-library'
import { activities } from '../releaseDetail'
import { WitchQueenDungeonHashes, LightfallDungeonHashes, FinalShapeDungeonHashes } from '../enums/dungeons'
import { Expansions } from '../enums/expansions'
import { BeyondLightSeasonHashes, EdgeOfFateSeasonHashes, FinalShapeEpisodeHashes, LightfallSeasonHashes, RenegadesSeasonHashes, ShadowkeepSeasonHashes, WitchQueenSeasonHashes } from '../enums/seasons'
import DungeonIcon from './DungeonIcon.vue'
import ExpansionIcon from './ExpansionIcon.vue'
import SeasonIcon from './SeasonIcon.vue'
import ActivityIndicator from './ActivityIndicator.vue'
import ProgressBadge from './ProgressBadge.vue'
import { ActivityRecord } from '../enums/activities'

const emit = defineEmits(['toggle', 'remove', 'loadFireteam'])
const props = withDefaults(defineProps<{
  account: Account,
  accounts: Account[],
  rotations: Record<string, string>,
  loading?: boolean,
  isOpen: Record<keyof ActivityRecord, boolean>,
  isDemo?: boolean
}>(), {
  loading: false,
  isDemo: false
})
const supporters = inject<Ref<Supporter[]>>('supporters')
const parallel = inject<Ref<Parallel[]>>('parallel')

const unloadedFireteamMembers = computed(() => props.account.fireteam.filter(x => !props.accounts.map(y => y.membershipId).includes(x)))
const canLoadFireteam = computed(() => unloadedFireteamMembers.value.length > 0 && !props.loading)
const anyCanLoadFireteam = computed(() => props.accounts.some((x, _, all) => !x.fireteam.every(y => all.map(w => w.membershipId).includes(y))))

const allCurrentExpansions = Expansions.Renegades | Expansions.EdgeOfFate | Expansions.FinalShape
                    | Expansions.Lightfall | Expansions.TheWitchQueen | Expansions.BeyondLight
                    | Expansions.Shadowkeep | Expansions.Forsaken
const hasAllCurrentExpansions = computed(() => ((props.account.versionsOwned ?? 0) & allCurrentExpansions) === allCurrentExpansions)

const hasShadowkeepDeluxe = computed(() => 
  ((props.account.versionsOwned ?? 0) & Expansions.Shadowkeep) === Expansions.Shadowkeep
  && props.account.seasonsOwned?.includes(ShadowkeepSeasonHashes.Undying)
  && props.account.seasonsOwned?.includes(ShadowkeepSeasonHashes.Dawn)
  && props.account.seasonsOwned?.includes(ShadowkeepSeasonHashes.Worthy)
  && props.account.seasonsOwned?.includes(ShadowkeepSeasonHashes.Arrivals)
)

const hasBeyondLightDeluxe = computed(() => 
  ((props.account.versionsOwned ?? 0) & Expansions.BeyondLight) === Expansions.BeyondLight
  && props.account.seasonsOwned?.includes(BeyondLightSeasonHashes.Hunt)
  && props.account.seasonsOwned?.includes(BeyondLightSeasonHashes.Chosen)
  && props.account.seasonsOwned?.includes(BeyondLightSeasonHashes.Splicer)
  && props.account.seasonsOwned?.includes(BeyondLightSeasonHashes.Lost)
)

const hasWitchQueenDeluxe = computed(() => 
  ((props.account.versionsOwned ?? 0) & Expansions.TheWitchQueen) === Expansions.TheWitchQueen
  && props.account.dungeonsOwned?.includes(WitchQueenDungeonHashes.Duality)
  && props.account.dungeonsOwned?.includes(WitchQueenDungeonHashes.SpireOfTheWatcher)
  && props.account.seasonsOwned?.includes(WitchQueenSeasonHashes.Risen)
  && props.account.seasonsOwned?.includes(WitchQueenSeasonHashes.Haunted)
  && props.account.seasonsOwned?.includes(WitchQueenSeasonHashes.Plunder)
  && props.account.seasonsOwned?.includes(WitchQueenSeasonHashes.Seraph)
) 

const hasLightfallDeluxe = computed(() => 
  ((props.account.versionsOwned ?? 0) & Expansions.Lightfall) === Expansions.Lightfall
  && props.account.dungeonsOwned?.includes(LightfallDungeonHashes.GhostsOfTheDeep)
  && props.account.dungeonsOwned?.includes(LightfallDungeonHashes.WarlordsRuin)
  && props.account.seasonsOwned?.includes(LightfallSeasonHashes.Defiance)
  && props.account.seasonsOwned?.includes(LightfallSeasonHashes.Deep)
  && props.account.seasonsOwned?.includes(LightfallSeasonHashes.Witch)
  && props.account.seasonsOwned?.includes(LightfallSeasonHashes.Wish)
)

const hasFinalShapeDeluxe = computed(() => 
  ((props.account.versionsOwned ?? 0) & Expansions.FinalShape) === Expansions.FinalShape
  && props.account.dungeonsOwned?.includes(FinalShapeDungeonHashes.VespersHost)
  && props.account.dungeonsOwned?.includes(FinalShapeDungeonHashes.SunderedDoctrine)
  && props.account.seasonsOwned?.includes(FinalShapeEpisodeHashes.Echoes)
  && props.account.seasonsOwned?.includes(FinalShapeEpisodeHashes.Revenant)
  && props.account.seasonsOwned?.includes(FinalShapeEpisodeHashes.Heresy)
)

const hasYearOfProphecyDeluxe = computed(() => 
  ((props.account.versionsOwned ?? 0) & Expansions.EdgeOfFate) === Expansions.EdgeOfFate
  && ((props.account.versionsOwned ?? 0) & Expansions.Renegades) === Expansions.Renegades
  && props.account.seasonsOwned?.includes(EdgeOfFateSeasonHashes.Reclamation)
  && props.account.seasonsOwned?.includes(RenegadesSeasonHashes.Lawless)
)

const hasAllCurrent = computed(() => hasAllCurrentExpansions.value
                              && props.account.dungeonsOwned?.includes(WitchQueenDungeonHashes.Duality)
                              && props.account.dungeonsOwned?.includes(WitchQueenDungeonHashes.SpireOfTheWatcher)
                              && props.account.dungeonsOwned?.includes(LightfallDungeonHashes.GhostsOfTheDeep)
                              && props.account.dungeonsOwned?.includes(LightfallDungeonHashes.WarlordsRuin)
                              && props.account.dungeonsOwned?.includes(FinalShapeDungeonHashes.VespersHost)
                              && props.account.dungeonsOwned?.includes(FinalShapeDungeonHashes.SunderedDoctrine))

const hasAllDeluxe = computed(() => ((props.account.versionsOwned ?? 0) & Expansions.CurseOfOsiris) === Expansions.CurseOfOsiris
                              && ((props.account.versionsOwned ?? 0) & Expansions.Warmind) === Expansions.Warmind
                              && ((props.account.versionsOwned ?? 0) & Expansions.AnnualPass) === Expansions.AnnualPass
                              && hasShadowkeepDeluxe.value && hasBeyondLightDeluxe.value && hasWitchQueenDeluxe.value
                              && hasFinalShapeDeluxe.value && hasLightfallDeluxe.value && hasYearOfProphecyDeluxe.value)

const hasNoContent = computed(() => (props.account?.versionsOwned ?? 0) < Expansions.Forsaken && !props.account.dungeonsOwned?.length && !props.account.seasonsOwned?.length)

const foundDonation = computed(() => {
  if (!supporters?.value) return undefined
  return supporters.value.find(x => x.linkedMembershipIds?.includes(props.account.membershipId)) || undefined
})
const foundParallel = computed(() => {
  if (!parallel?.value) return undefined
  return parallel.value.find(x => x.members.find(y => y.membershipId === props.account.membershipId)) || undefined
})

const wodLink = computed(() => `https://wastedondestiny.com/${props.account.membershipId}`)
const lrLink = computed(() => props.account.clan ? `https://loot.report/${props.account.clan.id}` : '')

function toggle(tag: keyof typeof props.isOpen) {
  emit('toggle', tag)
}

function remove(account: AccountResult) {
  emit('remove', account)
}

function loadFireteam() {
  emit('loadFireteam', unloadedFireteamMembers.value)
}
</script>

<template>
  <div class="flex flex-col w-72 bg-white text-black shadow-lg shadow-black/50 rounded-2xl px-2 py-3 mb-5 relative">
    <div class="flex items-center justify-between -mt-3 mb-1 w-72 -mx-2 rounded-t-2xl py-1 px-2 bg-zinc-200" v-if="isDemo">
      <span />
      <img src="../assets/close.svg" alt="Close button" class="h-4 w-4 opacity-75" />
    </div>
    <div class="flex items-center justify-between -mt-3 mb-1 w-72 -mx-2 rounded-t-2xl py-1 px-2 bg-zinc-200" v-else>
      <PlatformIcon
        :account="account"
        :supporting="foundDonation"
        :parallel="foundParallel"
      />
      <img @click="() => remove(account)" alt="Close button" src="../assets/close.svg" class="h-4 w-4 cursor-pointer opacity-75 hover:opacity-100" />
    </div>
    <div class="pb-1 mb-1 border-b-2 border-black/25">
      <h2 class="text-xl font-semibold">{{ account.displayName }}</h2>
      <Dropdown>
        <a class="no-underline opacity-75 hover:opacity-100 p-2" target="_blank" :href="wodLink">
          <img class="h-4 w-4 inline-block" src="../assets/twod.svg" /> Check time on Time Wasted on Destiny
        </a>
        <a v-if="lrLink" class="no-underline opacity-75 hover:opacity-100 p-2 border-t-2 border-zinc-500" target="_blank" :href="lrLink">
          <img class="h-4 w-4 inline-block" src="../assets/lootreport.svg" /> Check clan exotics on loot.report
        </a>
        <!-- <button class="text-left opacity-75 hover:opacity-100 p-2 border-t-2 border-zinc-500" @click="() => toggle('detailed')">
          <img class="h-4 w-4 inline-block" src="../assets/check.svg" /> View my detailed expansions
        </button> -->
        <a class="no-underline opacity-75 hover:opacity-100 p-2 border-t-2 border-zinc-500" target="_blank" href="https://www.bungie.net/7/en/User/Account/Privacy">
          <img class="h-4 w-4 inline-block" src="../assets/close.svg" /> Change my privacy settings
        </a>
      </Dropdown>
      <span v-if="canLoadFireteam" class="text-xs text-black flex items-center cursor-pointer opacity-75 hover:opacity-100" @click="loadFireteam">
        <img src="../assets/group.svg" class="h-5 w-5" />
        <span class="pl-2">Add fireteam ({{ unloadedFireteamMembers.length }} more guardian{{ unloadedFireteamMembers.length > 1 ? 's' : '' }})</span>
      </span>
      <div v-else-if="!loading && anyCanLoadFireteam" class="h-5" />
      <template v-if="!isDemo">
        <div class="h-10 flex items-center gap-2">
          <ProgressBadge v-if="foundParallel" name="Parallel" class="bg-zinc-800" description="Develop applications for Destiny or Destiny 2 and be recognized for it by Bungie" image="parallel.png" unlocked />
          
          <ProgressBadge v-if="foundDonation" name="Benefactor" class="bg-orange-600" inner-classes="!h-7 !w-7" description="Donate any amount towards Wasted on Destiny, BinarMorker or Karmageddon. Thank you!" image="seeker.png" unlocked :progress="foundDonation.tier" />
          
          <ProgressBadge v-if="hasAllDeluxe" name="Became Legend" class="bg-green-800" description="Own all paid expansions, seasons and dungeons ever released for Destiny 2" image="lore.png" unlocked />
          <ProgressBadge v-else-if="hasAllCurrent" name="Avant-Garde" class="bg-green-700" description="Own all currently available paid expansions, seasons and dungeons for Destiny 2" image="expert.png" unlocked />
          <ProgressBadge v-else-if="hasAllCurrentExpansions" name="Up to Date" class="bg-green-600" description="Own all paid expansions for Destiny 2" image="triumph.png" unlocked />
          
          <ProgressBadge v-if="hasNoContent" name="Free to Play" class="bg-zinc-800" description="Own no paid content whatsoever" image="season.png" unlocked />
        </div>
      </template>
    </div>
    <div class="flex flex-wrap justify-center -m-1">
      <div class="flex">
        <ExpansionIcon :expansion="Expansions.Forsaken" :account="account" :no-interact="isDemo" />
        <ExpansionIcon :expansion="Expansions.Shadowkeep" :account="account" :no-interact="isDemo" />
        <ExpansionIcon :expansion="Expansions.BeyondLight" :account="account" :no-interact="isDemo" />
        <ExpansionIcon :expansion="Expansions.Anniversary30th" :account="account" :no-interact="isDemo" />
        <div class="flex border-black/25">
          <ExpansionIcon :expansion="Expansions.TheWitchQueen" :account="account" :no-interact="isDemo" />
          <div class="py-2 -ml-4 flex">
            <DungeonIcon :dungeons="[WitchQueenDungeonHashes.Duality, WitchQueenDungeonHashes.SpireOfTheWatcher]" :account="account" :no-interact="isDemo" />
          </div>
        </div>
      </div>
      <div class="flex gap-2">
        <div class="flex border-black/25'">
          <ExpansionIcon :expansion="Expansions.Lightfall" :account="account" :no-interact="isDemo" />
          <div class="py-2 -ml-4 flex">
            <DungeonIcon :dungeons="[LightfallDungeonHashes.GhostsOfTheDeep, LightfallDungeonHashes.WarlordsRuin]" :account="account" :no-interact="isDemo" />
          </div>
        </div>
        <div class="flex border-black/25">
          <ExpansionIcon :expansion="Expansions.FinalShape" :account="account" :no-interact="isDemo" />
          <div class="py-2 -ml-4 flex">
            <DungeonIcon :dungeons="[FinalShapeDungeonHashes.VespersHost, FinalShapeDungeonHashes.SunderedDoctrine]" :account="account" :no-interact="isDemo" />
          </div>
        </div>
        <div class="flex":class="{ 'border-2 -m-[2px] border-yellow-500 shadow-yellow-500 shadow-[0_0_4px,inset_0_0_4px]': hasYearOfProphecyDeluxe, 'border-black/25': !hasYearOfProphecyDeluxe }">
          <ExpansionIcon :is-deluxe="hasYearOfProphecyDeluxe" :expansion="Expansions.EdgeOfFate" :account="account" :no-interact="isDemo" />
          <ExpansionIcon :is-deluxe="hasYearOfProphecyDeluxe" :expansion="Expansions.Renegades" :account="account" :no-interact="isDemo" />
          <div class="p-2 -ml-4 flex">
            <SeasonIcon :season="RenegadesSeasonHashes.MonumentOfTriumph" :account="account" :no-interact="isDemo" />
          </div>
        </div>
      </div>
    </div>
    <div class="flex flex-col py-1">
      <Collapsible name="RAIDS" :open="isOpen.raid" @toggle="() => toggle('raid')" :no-interact="isDemo">
        <ActivityIndicator v-for="activity in activities.raid" activity-type="raid" :accounts="accounts" :account="account" :activity="activity" :no-interact="isDemo" />
      </Collapsible>
      <Collapsible name="DUNGEONS" :open="isOpen.dungeon" @toggle="() => toggle('dungeon')" :no-interact="isDemo">
        <ActivityIndicator v-for="activity in activities.dungeon" activity-type="dungeon" :accounts="accounts" :account="account" :activity="activity" :no-interact="isDemo" />
      </Collapsible>
      <Collapsible name="FIRETEAM OPS" :open="isOpen.fireteam" @toggle="() => toggle('fireteam')" :no-interact="isDemo">
        <ActivityIndicator v-for="activity in activities.fireteam" activity-type="fireteam" :accounts="accounts" :account="account" :activity="activity" :no-interact="isDemo" />
      </Collapsible>
      <Collapsible name="PINNACLE OPS" :open="isOpen.pinnacle" @toggle="() => toggle('pinnacle')" :no-interact="isDemo" only-portal>
        <ActivityIndicator v-for="activity in activities.pinnacle" activity-type="pinnacle" :accounts="accounts" :account="account" :activity="activity" :no-interact="isDemo" />
      </Collapsible>
      <Collapsible name="ARENA OPS" :open="isOpen.arena" @toggle="() => toggle('arena')" :no-interact="isDemo" only-portal>
        <ActivityIndicator v-for="activity in activities.arena" activity-type="arena" :accounts="accounts" :account="account" :activity="activity" :no-interact="isDemo" />
      </Collapsible>
      <Collapsible name="SOLO OPS" :open="isOpen.solo" @toggle="() => toggle('solo')" :no-interact="isDemo" only-portal>
        <ActivityIndicator v-for="activity in activities.solo" activity-type="solo" :accounts="accounts" :account="account" :activity="activity" :no-interact="isDemo" />
      </Collapsible>
      <Collapsible name="CORE ACTIVITIES" :open="isOpen.core" @toggle="() => toggle('core')" :no-interact="isDemo">
        <ActivityIndicator v-for="activity in activities.core" activity-type="core" :accounts="accounts" :account="account" :activity="activity" :no-interact="isDemo" />
      </Collapsible>
      <Collapsible name="EXPANSION ACTIVITIES" :open="isOpen.expansion" @toggle="() => toggle('expansion')" :no-interact="isDemo">
        <ActivityIndicator v-for="activity in activities.expansion" activity-type="expansion" :accounts="accounts" :account="account" :activity="activity" :no-interact="isDemo" />
      </Collapsible>
    </div>
    <template v-if="isDemo">
      <TheTooltip class="absolute" style="top: 26px; left: 2px;">
        <div class="border-blue-500 border-2 rounded-md" style="height: 32px; width: 284px;" />
        <template #content>
          <p class="w-52">This is the guardian's display name. The #1234 numbers are the determinant that identifies all the unique Example players out there. Yours can be found in-game in the character selection screen.</p>
        </template>
      </TheTooltip>
      <TheTooltip class="absolute" style="top: 62px; left: 36px;">
        <div class="border-blue-500 border-2 rounded-md" style="height: 48px; width: 48px;" />
        <template #content>
          <p class="w-52">This is an expansion owned by this player. Hovering it gives information regarding which content is available upon purchasing the expansion.</p>
        </template>
      </TheTooltip>
      <TheTooltip class="absolute" style="top: 102px; left: 133px;">
        <div class="border-blue-500 border-2 rounded-md" style="height: 64px; width: 135px;" />
        <template #content>
          <p class="w-52">This is the current deluxe edition for the expansion, indicated by the golden border. If a user has the deluxe edition, that means they own all of that year's content. A user may have acquired all content of that year piece by piece and still have the golden border.</p>
        </template>
      </TheTooltip>
      <TheTooltip class="absolute" style="top: 214px; left: 4px;">
        <div class="border-blue-500 border-2 rounded-md" style="height: 36px; width: 280px;" />
        <template #content>
          <p class="w-52">This is a currently available activity indicator. Its status indicates whether this player can play it or not in their current fireteam (or the group your are currently looking at.)</p>
        </template>
      </TheTooltip>
      <TheTooltip class="absolute" style="top: 346px; right: 32px;">
        <div class="border-blue-500 border-2 rounded-md" style="height: 28px; width: 42px;" />
        <template #content>
          <p class="w-52">This is the player count (that you are looking at on this app) over the total number of players that make up a complete group in this activity. If more players are available than what can fit in the activity, the status may still be green even if not everyone has access to it.</p>
        </template>
      </TheTooltip>
      <TheTooltip class="absolute" style="top: 346px; left: 30px;">
        <div class="border-blue-500 border-2 rounded-md" style="height: 28px; width: 42px;" />
        <template #content>
          <p class="w-52">This activity is in rotation this week or is the latest of this category to have released. Therefore, it is farmable and will drop high-tiered loot. Check the activity descriptions to ensure you understand what conditions apply.</p>
        </template>
      </TheTooltip>
      <TheTooltip class="absolute" style="top: 570px; left: 2px;">
        <div class="border-blue-500 border-2 rounded-md" style="height: 24px; width: 284px;" />
        <template #content>
          <p class="w-52">This is a content category. Clicking it expands or collapses its content.</p>
        </template>
      </TheTooltip>
      <TheTooltip class="absolute" style="top: 810px; left: 4px;">
        <div class="border-blue-500 border-2 rounded-md" style="height: 36px; width: 280px;" />
        <template #content>
          <p class="w-52">In some rotations, activities may not be available until they enter their rotation week. If this is the case, it will still be displayed but will be less visible, like so.</p>
        </template>
      </TheTooltip>
    </template>
  </div>
</template>